import {
  AcademicGrade,
  TrackAssessmentStandard,
  TrackAssessmentResult,
  TrackAssessmentDimension,
  TrackAssessmentDimCriteria,
  TrackAssessmentDimScore,
} from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';

export const trackAssessmentApi = createApi({
  reducerPath: 'trackAssessmentApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['TrackAssessmentResult'],
  endpoints: (build) => ({
    getTrackAssessmentStandards: build.query<TrackAssessmentStandard[], undefined>({
      query: () => 'track-assessment/standards',
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    assessMyTrack: build.mutation<TrackAssessmentResult, undefined>({
      query: () => ({
        url: 'track-assessment/assess',
        method: 'GET',
      }),
      invalidatesTags: [{ type: 'TrackAssessmentResult' }],
    }),
    getMostRecentTrackAssessmentResult: build.query<TrackAssessmentResult, undefined>({
      query: () => 'track-assessment/most-recent',
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: [{ type: 'TrackAssessmentResult' }],
    }),
    getTrackAssessmentResult: build.query<TrackAssessmentResult, string>({
      query: (uuid) => `track-assessment/result/${uuid}`,
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getAllowReassess: build.query<{ allowReassess: boolean }, undefined>({
      query: () => 'track-assessment/allow-reassess',
      keepUnusedDataFor: 0, // No caching
    }),
    getDimensions: build.query<TrackAssessmentDimension[], undefined>({
      query: () => 'track-assessment/dimensions',
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getDimensionCriteria: build.query<
      TrackAssessmentDimCriteria[],
      { academicGradeId?: number; dimensionId?: number }
    >({
      query: ({ academicGradeId, dimensionId }) => {
        const params = new URLSearchParams();

        if (academicGradeId) params.append('academic_grade_id', String(academicGradeId));
        if (dimensionId) params.append('dimension_id', String(dimensionId));

        const queryString = params.toString();

        return `track-assessment/dimension-criteria${queryString ? `?${queryString}` : ''}`;
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    updateDimensionScore: build.mutation<
      TrackAssessmentDimScore,
      { scoreId: number; scoreLevel: string; justification?: string }
    >({
      query: ({ scoreId, scoreLevel, justification }) => ({
        url: `track-assessment/dimension-scores/${scoreId}`,
        method: 'PATCH',
        body: { score_level: scoreLevel, justification },
      }),
      invalidatesTags: [{ type: 'TrackAssessmentResult' }],
    }),
    getTrackAssessmentHistory: build.query<TrackAssessmentResult[], undefined>({
      query: () => 'track-assessment/history',
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: [{ type: 'TrackAssessmentResult' }],
    }),
    getAcademicGrades: build.query<AcademicGrade[], undefined>({
      query: () => 'academic-grades',
      keepUnusedDataFor: 60 * 60 * 24,
    }),
  }),
});

export const {
  useGetTrackAssessmentStandardsQuery,
  useAssessMyTrackMutation,
  useGetMostRecentTrackAssessmentResultQuery,
  useGetTrackAssessmentResultQuery,
  useGetAllowReassessQuery,
  useGetDimensionsQuery,
  useGetDimensionCriteriaQuery,
  useUpdateDimensionScoreMutation,
  useGetTrackAssessmentHistoryQuery,
  useGetAcademicGradesQuery,

  usePrefetch: usePrefetchTrackAssessment,

  util: { resetApiState },
} = trackAssessmentApi;
